import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedNumber — counts up from 0 to `end` when the element enters the viewport.
 * Supports numeric strings like "27+" or "24/7" by only animating the leading number.
 */
const AnimatedNumber = ({ value, duration = 1400, className = '' }) => {
    const ref = useRef(null);
    const [display, setDisplay] = useState(value);
    const started = useRef(false);

    useEffect(() => {
        // Parse the number portion + suffix
        const match = String(value).match(/^(\d+)(.*)$/);
        if (!match) return; // non-numeric like "24/7" — just show as-is
        const target = parseInt(match[1], 10);
        const suffix = match[2] || '';

        // Reset for parse
        setDisplay(`0${suffix}`);

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !started.current) {
                    started.current = true;
                    const startTime = performance.now();
                    const tick = (now) => {
                        const t = Math.min(1, (now - startTime) / duration);
                        const eased = 1 - Math.pow(1 - t, 3);
                        const current = Math.round(target * eased);
                        setDisplay(`${current}${suffix}`);
                        if (t < 1) requestAnimationFrame(tick);
                    };
                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.4 },
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, [value, duration]);

    return (
        <span ref={ref} className={className}>
            {display}
        </span>
    );
};

export default AnimatedNumber;
